/* ---------------------------------------------------------------------------
   The programme: what was planned, what happened, and the gap.

   FIDIC Sub-Clause 4.21(h) — a comparison of actual against planned progress —
   and the item the ROADMAP decision of 2026-09-16 reserved. Two consumers, as
   with src/screens/works.ts: Construction Scheduler's own screen
   (src/screens/schedule.ts), where the matching is done, and section (h) of
   Construction Monitoring's period report.

   It was on the bridge record until the site became four applications. A
   baseline and a variance table are a planner's document, read at a desk, and
   they sat on a page about the condition of a structure — 204 rows of it, under
   the change log. The record links across to this instead.

   ══ WHAT THIS SCREEN IS CAREFUL ABOUT ══

   **It never presents a proposal as a match.** The register can see that a
   scheduled activity called "Bent-2 Bent Cap" and a component called "Bent-2
   Bent Cap" are probably the same work. It says so, in a row marked as a
   suggestion, with a button — and until somebody presses it the activity is
   unmatched and is excluded from every figure. That is the decision, kept.

   **It never infers a finish.** An activity with entries against it is in
   progress until a person declares it finished; the register knows when the
   last entry arrived and does not know that no more are coming. So there is a
   "Finished" control, and a variance for the activities that have used it.

   **It gives two percentages and never one.** What has been declared complete
   and what has been started-or-completed. The truth is between them, and a
   single figure would have to guess how far through an in-progress activity a
   site is.

   **It says what the weighting is, every time it prints one.** By planned
   duration, not by value — see the header of src/data/variance.ts for why a
   value-weighted curve is not available and what would make it so.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type Child } from '../core/dom.ts';
import { actions, can, ganttShown, type State } from '../core/store.ts';
import { button, card, cardHead, emptyState, fact, tag } from '../components/widgets.ts';
import { prettyDate } from '../data/derive.ts';
import { baselineComponents, proposeMatch } from '../data/programme.ts';
import { EVERY_DAY, type WorkCalendar } from '../data/calendar.ts';
import { completionDate } from '../data/contract.ts';
import { forecastFor, slipWord, type Forecast, type ForecastRow } from '../data/forecast.ts';
import { ganttSlot, ganttStatus } from './ganttBlock.ts';
import { programmeCalendar, type CalendarItem } from './programmeCalendar.ts';
import {
  completionOf,
  entriesAsOf,
  lateRows,
  latenessOf,
  matchesAsOf,
  progressCurve,
  signedDays,
  varianceFor,
  type Baseline,
  type CountableEntry,
  type VarianceRow,
} from '../data/variance.ts';
import { componentChoices, idForLabel } from '../data/componentTree.ts';
import { holdsByActivity } from '../data/siteQuality.ts';
import { sCurveChart } from './progressCharts.ts';
import { sv } from '../components/chart.ts';
import type { SiteItem } from '../data/types.ts';

/** Today, as a calendar day. The one place this module reads the clock. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Only accepted entries count toward a variance.
 *
 * A report goes to a client, and an entry nobody has checked is not something
 * to tell them the programme is on time on the strength of. The record's own
 * card uses the same rule, so the two cannot disagree about how far through
 * the works are.
 *
 * As at `asOf` where one is given — the report's period end (LC-51) — and as
 * at today otherwise. The label and the model's id both go (LC-14), and the
 * kind of work, so an entry starts only the activity it is work on (LC-41).
 */
export function countable(s: Readonly<State>, asOf: string = today()): CountableEntry[] {
  return entriesAsOf(s.progress, asOf).map((e) => ({
    component: e.component,
    componentId: e.componentId ?? null,
    occurredOn: e.occurredOn,
    activity: e.activity,
  }));
}

/** The calendar a baseline's durations count on: its own, the department's copy, or every day. */
export function calendarOf(s: Readonly<State>, b: Baseline): WorkCalendar {
  return b.calendar ?? s.calendars.find((c) => c.id === b.calendarId) ?? EVERY_DAY;
}

/**
 * The forecast as at a day (LC-44): the accepted baseline's logic re-solved
 * from what was accepted by then. Null with no baseline in force.
 */
export function forecastOf(s: Readonly<State>, asOf: string = today()): Forecast | null {
  if (s.baseline === null) return null;
  const rows = varianceFor(s.baseline, matchesAsOf(s.matches, asOf), countable(s, asOf));
  return forecastFor(s.baseline, rows, asOf, calendarOf(s, s.baseline));
}

/**
 * The contract's completion date as at a day: the time for completion plus the
 * extensions accepted by then. Null where the baseline carries no contract.
 */
export function contractCompletion(s: Readonly<State>, asOf: string = today()): string | null {
  const c = s.baseline?.contract ?? null;
  if (c === null) return null;
  const granted = s.extensions.filter((e) => e.state === 'accepted' && (e.decidedAt ?? '').slice(0, 10) <= asOf);
  return completionDate(c, granted);
}

/* --------------------------- the Scheduler's two views --------------------------- */

/*
   CONSTRUCTION SCHEDULER SHOWS ONE THING AT A TIME, AND LETS IT HAVE THE PAGE.
   The ribbon offers the Gantt and the table of planned activities; whichever is
   in front fills the screen, and nothing else is drawn around it. The figures,
   the late list and the notes that the card used to carry are still on section
   (h) of the construction report, which is where they are read and printed.
*/

/**
 * Why there is no view to draw, or null where there is one.
 *
 * A structure with no accepted baseline gets the one way on — taking one, or
 * accepting the one waiting — where the reader may, and a sentence where they
 * may not. A proposed baseline is never drawn as the programme (LC-40).
 */
function noProgramme(_bridgeId: string, bridgeName: string, s: Readonly<State>, needsProgress: boolean): Child {
  if (s.baseline === null) {
    const p = s.proposedBaseline;
    if (p !== null) {
      return h(
        'div',
        { class: 'schedfill__empty' },
        emptyState(`${p.name} is waiting for acceptance.`),
        can(s, 'programme.approve')
          ? button('Accept baseline', {
              primary: true,
              iconPath: ICONS.check,
              disabled: s.baselineBusy,
              onClick: () => void actions.acceptProposedBaseline(),
            })
          : null,
        s.baselineNote ? h('div', { class: 'dlg__note' }, h('span', { text: s.baselineNote })) : null,
      );
    }
    const mayManage = can(s, 'programme.manage');
    return h(
      'div',
      { class: 'schedfill__empty' },
      emptyState('No baseline.'),
      mayManage
        ? button('Take a baseline', {
            primary: true,
            iconPath: ICONS.plus,
            disabled: s.baselineBusy,
            title: `Freeze the current programme from ${bridgeName}’s 3D model, with its contract`,
            onClick: () => actions.openBaselineForm(),
          })
        : null,
      s.baselineNote ? h('div', { class: 'dlg__note' }, h('span', { text: s.baselineNote })) : null,
    );
  }
  if (needsProgress && s.progressRead !== 'read') {
    return h(
      'div',
      { class: 'schedfill__empty' },
      emptyState(
        'The site record for this structure has not been read yet, so there is nothing to compare the programme against.',
      ),
    );
  }
  return null;
}

/**
 * The frozen baseline as a Gantt, filling the page.
 *
 * THE SAME SLOT `syncGantt` MOUNTS INTO, and the same covers over it while it
 * loads or fails — see `ganttPanel` for why the slot is always in the tree.
 * The full-view bar is left out: this view is already the size of the page.
 */
export function programmeGantt(bridgeId: string, bridgeName: string, s: Readonly<State>): Child {
  const none = noProgramme(bridgeId, bridgeName, s, false);
  if (none) return none;
  const status = ganttStatus();

  return h(
    'div',
    { class: 'gantt gantt--fill' },
    ganttSlot(),
    status.state === 'loading'
      ? h(
          'div',
          { class: 'gantt__cover' },
          h('div', { class: 'mdl__spinner' }),
          h('div', { class: 'gantt__cover-title', text: 'Loading the schedule view…' }),
        )
      : null,
    status.state === 'failed'
      ? h(
          'div',
          { class: 'gantt__cover' },
          h('div', { class: 'gantt__cover-title', text: 'The programme could not be drawn' }),
          h('div', { class: 'gantt__cover-sub', text: status.message }),
        )
      : null,
  );
}

/** The frozen baseline on a month calendar, critical activities marked. */
export function programmeGanttCalendar(bridgeId: string, bridgeName: string, s: Readonly<State>): Child {
  const none = noProgramme(bridgeId, bridgeName, s, false);
  if (none) return none;
  const items: CalendarItem[] = s.baseline!.activities
    .filter((a) => !a.isGroup && a.plannedStart && a.plannedFinish)
    .map((a) => ({
      id: a.activityId,
      name: a.name,
      start: a.plannedStart,
      finish: a.plannedFinish,
      tone: a.floatDays === 0 ? 'crit' : 'plan',
      title: a.floatDays === 0 ? 'Critical: no float' : `${Math.round(a.floatDays)} d float`,
    }));
  return programmeCalendar(s, items, [
    { tone: 'plan', label: 'Planned' },
    { tone: 'crit', label: 'Critical' },
  ]);
}

/** Every planned activity on a month calendar, coloured by where it has got to on site. */
export function programmeActivitiesCalendar(bridgeId: string, bridgeName: string, s: Readonly<State>): Child {
  const none = noProgramme(bridgeId, bridgeName, s, true);
  if (none) return none;
  const rows = varianceFor(s.baseline!, s.matches, countable(s));
  const forecast = new Map(
    forecastFor(s.baseline!, rows, today(), calendarOf(s, s.baseline!)).rows.map((f) => [f.activityId, f]),
  );
  const items: CalendarItem[] = rows
    .filter((r) => r.plannedDays > 0)
    .map((r) => {
      const f = forecast.get(r.activityId);
      const late = r.state !== 'complete' && (f?.slipDays ?? 0) > 0;
      const tone: CalendarItem['tone'] =
        r.state === 'complete' ? 'good' : late ? 'late' : r.state === 'in-progress' ? 'active' : 'idle';
      const said =
        r.state === 'complete'
          ? `Complete${r.completedOn ? ` on ${prettyDate(r.completedOn)}` : ''}`
          : r.state === 'unmatched'
            ? 'Not matched to the record'
            : r.state === 'in-progress'
              ? `In progress since ${prettyDate(r.firstEntry)}`
              : 'Not started';
      const slip = f && r.state !== 'complete' ? ` · forecast finish ${prettyDate(f.forecastFinish)}` : '';
      return { id: r.activityId, name: r.name, start: r.plannedStart, finish: r.plannedFinish, tone, title: said + slip };
    });
  return programmeCalendar(s, items, [
    { tone: 'idle', label: 'Not started' },
    { tone: 'active', label: 'In progress' },
    { tone: 'late', label: 'Forecast late' },
    { tone: 'good', label: 'Complete' },
  ]);
}

/** Every planned activity against what the site has recorded, filling the page. */
export function programmeActivities(bridgeId: string, bridgeName: string, s: Readonly<State>): Child {
  const none = noProgramme(bridgeId, bridgeName, s, true);
  if (none) return none;
  const rows = varianceFor(s.baseline!, s.matches, countable(s));
  const forecast = forecastFor(s.baseline!, rows, today(), calendarOf(s, s.baseline!));

  return h(
    'div',
    { class: 'schedfill__table' },
    s.baselineNote ? h('div', { class: 'dlg__note' }, h('span', { text: s.baselineNote })) : null,
    varianceTable(
      rows.filter((r) => r.plannedDays > 0),
      s,
      can(s, 'programme.manage'),
      wbsPaths(s.baseline!),
      new Map(forecast.rows.map((f) => [f.activityId, f])),
      predecessorsOf(s.baseline!),
    ),
  );
}

/* --------------------------- the bars --------------------------- */

/**
 * The two buttons that govern the bars, for whichever card is drawing them.
 *
 * ONE SET, TWO HEADS. The record's Programme card and section (h) of the
 * construction report both offer the Gantt and both read the same two flags,
 * so the buttons are written once: two copies would be two chances for the
 * record to say "Hide the bars" while the report says "Show" about the very
 * same state.
 *
 * FULL VIEW IS OFFERED ONLY WHILE THE BARS ARE OUT. There is nothing to throw
 * across the window until the fold is open, and a button that would first
 * unfold and then expand is two acts wearing one label.
 */
function ganttControls(s: Readonly<State>): Child[] {
  const name = s.baseline?.name ?? 'the baseline';

  return [
    button(ganttShown(s) ? 'Hide the bars' : 'Show the bars', {
      sm: true,
      iconPath: ICONS.gantt,
      title: ganttShown(s)
        ? 'Fold the programme back to the figures and the tables'
        : `Draw ${name} as a Gantt — the modeller's own schedule view`,
      onClick: () => actions.toggleGantt(),
    }),
    ganttShown(s)
      ? button('Full view', {
          sm: true,
          iconPath: ICONS.expand,
          title:
            'Fill the window with the programme. A few hundred activities do not fit a ' +
            'card, and this is the same view in a box the size of the screen — Esc brings ' +
            'it back.',
          onClick: () => actions.toggleGanttFull(),
        })
      : null,
  ];
}

/**
 * The frozen baseline drawn as a Gantt, in the modeller's own schedule view.
 *
 * THE SLOT IS ALWAYS IN THE TREE while the fold is open, including during the
 * load and after a failure. It is what `syncGantt` looks for after the paint,
 * and a slot drawn only once the status said "ready" could never become ready
 * — there would be nothing to mount into. So the status is drawn OVER it
 * rather than instead of it.
 *
 * AND SO IS THE FULL-VIEW BAR, for a harder reason. The reconciler in
 * src/core/dom.ts matches children BY POSITION and leaves a `data-keep` slot's
 * contents alone only where the element in that position was the same slot
 * last paint. A bar drawn in front of the slot only while full view is on
 * would, the moment it appeared, put itself where the slot had been — and the
 * slot, shunted along one, would be diffed against a plain div and have the
 * whole mounted schedule view torn out of it. So it is always drawn, and CSS
 * decides whether it shows.
 *
 * WHAT THE TWO NOTES SAY, and both of them are the card being honest about a
 * decision made elsewhere rather than apologising:
 *
 *   · the bars are the plan as frozen, not the model's plan as it stands now.
 *     The variance above was measured against this and against nothing else;
 *     see the header of src/data/frozenSchedule.ts;
 *   · there are no dependency arrows, because a baseline stores solved dates
 *     and deliberately not the links that produced them — the whole argument
 *     of migrations/0032_baselines.sql.
 */
function ganttPanel(s: Readonly<State>): HTMLElement {
  const b = s.baseline;
  const status = ganttStatus();

  return h(
    'div',
    { class: 'works__sub' },
    h('div', { class: 'works__sub-head', text: 'The programme as frozen' }),

    h(
      'div',
      { class: ['gantt', s.ganttFull && 'gantt--full'] },

      /* THE BAR FULL VIEW IS READ FROM, drawn in both states and shown in one.
         It carries what the card head carried and the overlay now covers: which
         baseline this is, and the way out. */
      h(
        'div',
        { class: 'gantt__bar' },
        h('span', { class: 'gantt__bar-title', text: 'The programme as frozen' }),
        h('span', {
          class: 'gantt__bar-sub',
          text: b
            ? `${b.name} · ${prettyDate(b.takenAt.slice(0, 10))}` +
              `${b.takenBy ? ` · ${b.takenBy}` : ''}`
            : '',
        }),
        h('span', { class: 'spacer' }),
        h('span', { class: 'gantt__bar-sub', text: 'Esc' }),
        button('Back to the card', {
          sm: true,
          iconPath: ICONS.shrink,
          title: 'Put the programme back in the Programme card, at the row you are on',
          onClick: () => actions.toggleGanttFull(),
        }),
      ),

      ganttSlot(),
      status.state === 'loading'
        ? h(
            'div',
            { class: 'gantt__cover' },
            h('div', { class: 'mdl__spinner' }),
            h('div', { class: 'gantt__cover-title', text: 'Loading the schedule view…' }),
            h('div', {
              class: 'gantt__cover-sub',
              text: 'The modeller’s Gantt and its solver — fetched once per session',
            }),
          )
        : null,
      status.state === 'failed'
        ? h(
            'div',
            { class: 'gantt__cover' },
            h('div', { class: 'gantt__cover-title', text: 'The programme could not be drawn' }),
            h('div', { class: 'gantt__cover-sub', text: status.message }),
          )
        : null,
    ),

    h('div', {
      class: 'dlg__hint',
      text: b
        ? `${b.name}, as frozen ${prettyDate(b.takenAt.slice(0, 10))}` +
          `${b.takenBy ? ` by ${b.takenBy}` : ''}`
        : '',
    }),
  );
}

/* --------------------------- the table --------------------------- */

const STATE_WORD: Record<VarianceRow['state'], string> = {
  unmatched: 'Not matched',
  'not-started': 'Not started',
  'in-progress': 'In progress',
  complete: 'Complete',
};

/**
 * Where each activity sits in the model's work breakdown: the names of its
 * ancestors, outermost first.
 *
 * THIS IS WHAT TELLS SIX "P-1"s APART. The modeller names a pile P-1 in every
 * footing, and the name alone says nothing about which footing of which bent;
 * the chain of groups above it does. The single root every baseline has (the
 * model itself) is left off, because it is the same on every row.
 */
function wbsPaths(baseline: Baseline): Map<string, string[]> {
  const byId = new Map(baseline.activities.map((a) => [a.activityId, a]));
  const roots = baseline.activities.filter((a) => a.parentId === null);
  const dropRoot = roots.length === 1 ? roots[0]!.activityId : null;

  const paths = new Map<string, string[]>();
  for (const a of baseline.activities) {
    const chain: string[] = [];
    const seen = new Set<string>();
    let at = a.parentId !== null ? byId.get(a.parentId) : undefined;
    /* `seen` guards a malformed parent loop, which would otherwise hang the
       paint. A baseline is frozen from the modeller and should never hold one. */
    while (at && !seen.has(at.activityId)) {
      seen.add(at.activityId);
      if (at.activityId !== dropRoot) chain.unshift(at.name);
      at = at.parentId !== null ? byId.get(at.parentId) : undefined;
    }
    paths.set(a.activityId, chain);
  }
  return paths;
}

/** A work-breakdown path, with the immediate parent — the useful part — in weight. */
function wbsCell(path: readonly string[]): HTMLElement {
  if (path.length === 0) return h('td', { class: 'muted', text: '—' });
  const parent = path[path.length - 1]!;
  const outer = path.slice(0, -1);
  return h(
    'td',
    { class: 'prog__wbs', title: path.join(' › ') },
    outer.length ? h('span', { class: 'muted', text: `${outer.join(' › ')} › ` }) : null,
    h('strong', { text: parent }),
  );
}

/** One thing an activity can be matched to: a part of the model, or a component the site has named. */
interface MatchChoice {
  key: string;
  name: string;
  id: string | null;
  text: string;
}

/**
 * What an activity can be matched to (LC-41): the model's own components, one
 * per part where it has ids, wherever the 3D model has reported its tree — so a
 * programme can be matched before a single entry exists — and then every
 * component the site has recorded work against that the tree does not name.
 */
export function matchChoices(s: Readonly<State>): MatchChoice[] {
  const out: MatchChoice[] = componentChoices(s.heroComponentTree ?? []).map((c) => ({
    key: c.id ?? `name:${c.name}`,
    name: c.name,
    id: c.id,
    text: c.text,
  }));
  /* Then what the programme itself says it builds, which needs no 3D view. */
  const keys = new Set(out.map((c) => c.key));
  for (const c of baselineComponents(s.baseline?.activities ?? [])) {
    const key = c.id ?? `name:${c.name}`;
    if (keys.has(key)) continue;
    keys.add(key);
    out.push({ key, name: c.name, id: c.id, text: c.text });
  }
  const names = new Set(out.map((c) => c.name));
  for (const e of s.progress) {
    if (!e.component || names.has(e.component)) continue;
    names.add(e.component);
    out.push({ key: `name:${e.component}`, name: e.component, id: idForLabel(s.progress, e.component), text: e.component });
  }
  return out;
}

/**
 * Each activity's predecessors as a reader writes them — `A1490 +13`,
 * `A1310 SS +13` — from the frozen links (LC-44). Finish-to-start is left
 * unsaid, as a programme leaves it.
 */
function predecessorsOf(b: Baseline): Map<string, string> {
  const out = new Map<string, string[]>();
  for (const l of b.links ?? []) {
    const lag = l.lagDays === 0 ? '' : ` ${l.lagDays > 0 ? '+' : ''}${l.lagDays}`;
    const text = `${l.from}${l.type === 'FS' ? '' : ` ${l.type}`}${lag}`;
    out.set(l.to, [...(out.get(l.to) ?? []), text]);
  }
  return new Map([...out].map(([k, v]) => [k, v.join(', ')]));
}

function varianceTable(
  rows: readonly VarianceRow[],
  s: Readonly<State>,
  mayManage: boolean,
  paths: ReadonlyMap<string, string[]>,
  forecast: ReadonlyMap<string, ForecastRow> = new Map(),
  preds: ReadonlyMap<string, string> = new Map(),
): HTMLElement {
  /* What a proposal is drawn from and what the picker offers: the model's parts
     and the components entries have been recorded against. */
  const choices = matchChoices(s);
  const components = [...new Set(choices.map((c) => c.name))];
  const asOf = today();

  return h(
    'table',
    { class: 'tbl prog__tbl' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'ID' }),
        h('th', { class: 'prog__act', text: 'Activity' }),
        h('th', { text: 'In the model' }),
        h('th', { class: 'num', text: 'Planned' }),
        h('th', { class: 'num', text: 'Float' }),
        h('th', { class: 'num', text: 'Forecast finish' }),
        h('th', { text: 'Recorded against' }),
        h('th', { class: 'num', text: 'First entry' }),
        h('th', { class: 'num', text: 'Variance' }),
        h('th', { text: '' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...rows.map((r) =>
        varianceRow(r, paths.get(r.activityId) ?? [], components, choices, asOf, mayManage, s, forecast.get(r.activityId), preds.get(r.activityId)),
      ),
    ),
  );
}

function varianceRow(
  r: VarianceRow,
  path: readonly string[],
  components: readonly string[],
  choices: readonly MatchChoice[],
  asOf: string,
  mayManage: boolean,
  s: Readonly<State>,
  f: ForecastRow | undefined,
  after: string | undefined,
): HTMLElement {
  /* The activity's own component where the programme knows it; else by name. */
  const act = r.state === 'unmatched' ? s.baseline?.activities.find((a) => a.activityId === r.activityId) : undefined;
  const proposal =
    r.state === 'unmatched'
      ? proposeMatch(act ?? { name: r.name }, components, (name) => choices.find((c) => c.name === name)?.id ?? idForLabel(s.progress, name))
      : null;
  const lateBy = latenessOf(r, asOf);

  return h(
    'tr',
    { class: [r.state === 'complete' && 'is-muted'], 'data-activity': r.activityId },

    h('td', { class: 'muted', style: 'padding-left:14px', text: r.activityId }),
    /* One line, clipped; the whole of it on hover. */
    h(
      'td',
      { class: 'prog__act', title: after ? `${r.name}
after ${after}` : r.name },
      h('span', { text: r.name }),
      after ? h('span', { class: 'muted plan__wbs', 'data-after': after, text: ` · after ${after}` }) : null,
    ),
    wbsCell(path),

    h('td', {
      class: 'num',
      text: `${prettyDate(r.plannedStart)} → ${prettyDate(r.plannedFinish)}`,
    }),

    /* Zero float is the critical path and is the number a reader scans for. */
    h(
      'td',
      { class: 'num' },
      r.floatDays === 0
        ? tag('critical', 3, 'No float: a day late here is a day late on the whole programme.')
        : h('span', { text: `${Math.round(r.floatDays)} d` }),
    ),

    /* The re-solve from the status date (LC-44): where this activity is now
       forecast to finish, and whether it is on today's critical path. */
    h(
      'td',
      { class: 'num', 'data-forecast': f?.forecastFinish ?? '' },
      f
        ? [
            h('span', { text: prettyDate(f.forecastFinish) }),
            f.critical ? tag('critical now', 3, 'On the critical path as it stands today') : null,
          ]
        : h('span', { class: 'muted', text: '—' }),
    ),

    h(
      'td',
      {},
      r.component
        ? h('span', { text: r.component })
        : proposal
          ? /* A SUGGESTION, DRAWN AS ONE. Until somebody presses the button the
               activity is unmatched and counts toward nothing. */
            h(
              'span',
              {},
              h('span', { class: 'prog__proposal', text: proposal.component }),
              h('span', { class: 'works__stat', text: 'suggested' }),
            )
          : h('span', { class: 'muted', text: 'Not matched' }),
    ),

    h('td', { class: 'num', text: r.firstEntry ? prettyDate(r.firstEntry) : '—' }),

    h(
      'td',
      { class: 'num' },
      r.state === 'complete' && r.finishVarianceDays !== null
        ? h('span', {
            class: r.finishVarianceDays > 0 ? 'works__stat works__stat--draft' : 'works__stat',
            text: varianceWord(r.finishVarianceDays),
          })
        : lateBy > 0
          ? h('span', { class: 'works__stat works__stat--draft', text: `${lateBy} d late` })
          : r.startVarianceDays !== null
            ? h('span', { text: varianceWord(r.startVarianceDays) })
            : h('span', { class: 'muted', text: STATE_WORD[r.state] }),
    ),

    h('td', {}, mayManage ? rowActions(r, proposal, choices, s) : null),
  );
}

/** Days as a phrase. Zero is "on time", which is a statement rather than a blank. */
function varianceWord(days: number): string {
  if (days === 0) return 'on time';
  return days > 0 ? `${days} d late` : `${-days} d early`;
}

/**
 * The picker a person matches an activity with (LC-41). One row at a time: a
 * programme of two hundred activities with a list of two hundred parts on every
 * row would be forty thousand options drawn on each paint.
 */
function matchPicker(r: VarianceRow, choices: readonly MatchChoice[]): HTMLElement {
  return h(
    'span',
    { class: 'prog__acts', 'data-picker': r.activityId },
    h(
      'select',
      {
        class: 'select select--sm',
        'aria-label': `The component ${r.name} is the work on`,
        onchange: (e: Event) => {
          const c = choices.find((x) => x.key === (e.target as HTMLSelectElement).value);
          if (c) void actions.matchActivity(r.activityId, r.name, c.name, null, c.id);
        },
      },
      h('option', { value: '', selected: true, text: choices.length ? 'Match to…' : 'No components to match to' }),
      ...choices.map((c) => h('option', { value: c.key, text: c.text })),
    ),
    button('Cancel', { sm: true, onClick: () => actions.pickMatch(null) }),
  );
}

function rowActions(r: VarianceRow, proposal: { component: string; componentId: string | null } | null, choices: readonly MatchChoice[], s: Readonly<State>): Child {
  if (r.state === 'unmatched') {
    if (s.matchPick === r.activityId) return matchPicker(r, choices);
    return h(
      'span',
      { class: 'prog__acts' },
      proposal
        ? button('Confirm', {
            sm: true,
            title: `Record that “${proposal.component}” is the work this activity describes`,
            /* With the model's id (LC-14), so the match and the entries name the
               same part, not merely the same word. */
            onClick: () =>
              void actions.matchActivity(
                r.activityId,
                r.name,
                proposal.component,
                null,
                proposal.componentId,
              ),
          })
        : h('span', {
            class: 'muted',
            title:
              'No component of this structure has the same name, or more than one does. This one ' +
              'needs a person.',
            text: 'needs a person',
          }),
      button('Match…', {
        sm: true,
        title: 'Choose the component of the model this activity is the work on',
        onClick: () => actions.pickMatch(r.activityId),
      }),
    );
  }

  if (r.state === 'complete') {
    return button('Reopen', {
      sm: true,
      title: 'Withdraw the declared finish — the entries and the plan both stand',
      onClick: () =>
        void actions.matchActivity(r.activityId, r.name, r.component ?? '', null, r.componentId),
    });
  }

  /* MATCHED AND NOT DECLARED FINISHED. The one control that turns a latest
     entry into a finish, and it is a person saying so. */
  return h(
    'span',
    { class: 'prog__acts' },
    button('Finished', {
      sm: true,
      title: r.latestEntry
        ? `Declare this finished on ${r.latestEntry}, the date of its latest entry`
        : 'Declare this finished today',
      onClick: () =>
        void actions.matchActivity(
          r.activityId,
          r.name,
          r.component ?? '',
          r.latestEntry ?? today(),
          r.componentId,
        ),
    }),
    button('Unmatch', {
      sm: true,
      title: 'Withdraw the match. Nothing about the works is lost.',
      onClick: () => void actions.unmatchActivity(r.activityId),
    }),
  );
}

/* --------------------------- the report's section (h) --------------------------- */

/**
 * Actual against planned, for the report.
 *
 * ONE STRUCTURE, AND IT SAYS SO. A baseline belongs to a structure and the
 * report covers a department over a period; on Proposal 1 — one new bridge —
 * those are the same thing, and on a register of fifty they are not. Rather
 * than silently reporting on whichever structure happens to be selected, the
 * section names it and states the limit.
 */
export function varianceSection(
  s: Readonly<State>,
  _bridgeName: string,
  /* IN THE PROPERTY PANEL, Construction Scheduler's Variance page: no report
     numbering, and no bars — the Gantt is the content panel's, and two slots
     for `syncGantt` on one page would be one too many. */
  panel = false,
  /* THE DAY IT IS MEASURED AS AT (LC-51). The report passes its period's end, so
     an August report printed in October reads as it did in August: entries
     accepted since, and finishes declared since, are not counted. Today where
     none is given, and never later than today. */
  asAt: string | null = null,
): HTMLElement {
  const title = panel ? 'Actual against planned' : '(h) Actual against planned progress';
  const asOf = asAt !== null && asAt < today() ? asAt : today();
  if (s.baseline === null) {
    return card(
      {},
      cardHead(title, undefined, tag('Not available', 2)),
      h('p', {
        class: 'wrep__gap',
        text: 'No baseline has been frozen.',
      }),
    );
  }

  /* A baseline frozen after the day measured did not exist on it. */
  if (s.baseline.takenAt.slice(0, 10) > asOf) {
    return card(
      {},
      cardHead(title, undefined, tag('Not available', 2)),
      h('p', {
        class: 'wrep__gap',
        text: `The baseline was frozen on ${prettyDate(s.baseline.takenAt.slice(0, 10))}, after ${prettyDate(asOf)}.`,
      }),
    );
  }

  const rows = varianceFor(s.baseline, matchesAsOf(s.matches, asOf), countable(s, asOf));
  const done = completionOf(rows);
  const late = lateRows(rows, asOf);
  const curve = progressCurve(rows, asOf);

  const lateTable = late.length > 0 ? lateList(late, asOf) : null;
  /* The re-solve from the status date (LC-44), against the baseline and the
     contract: the one figure that says whether the bridge will be late. */
  const forecast = forecastFor(s.baseline, rows, asOf, calendarOf(s, s.baseline));
  const outlook = forecastBlock(forecast, contractCompletion(s, asOf));

  /* ON THE VARIANCE PAGE, the forecast and the two tables, each a fold of its
     own with no card round them — the panel's title already says what the
     page is. */
  if (panel) {
    return h(
      'div',
      { class: 'plan' },
      fold(s, 'variance-forecast', 'Forecast completion', forecastGlance(forecast, contractCompletion(s, asOf))),
      curve.length > 0 ? fold(s, 'variance-curve', 'Planned against actual, cumulative', curveGlance(curve)) : null,
      fold(
        s,
        'variance-late',
        'Behind the programme',
        late.length > 0 ? lateGlance(late, asOf) : h('p', { class: 'muted plan__none', text: 'Nothing is behind the programme.' }),
      ),
    );
  }

  return card(
    {},
    cardHead(
      title,
      undefined,
      /* THE SAME FOLD AS THE RECORD'S, AND THE SAME FLAG BEHIND IT. Two
         independent toggles would let the bars be open on one screen and shut
         on the other, and `syncGantt` mounts into whichever slot the paint
         left — so the flag is what keeps exactly one of them on screen. The
         two are never drawn at once: the record and the report are different
         screens. */
      ...ganttControls(s),
    ),

    h(
      'div',
      { class: 'works__totals' },
      h('span', { class: 'works__stat', text: `${done.declaredPercent}% declared complete` }),
      h('span', { class: 'works__stat', text: `${done.startedPercent}% started or complete` }),
      h('span', {
        class: late.length ? 'works__stat works__stat--draft' : 'works__stat',
        text: `${late.length} behind programme`,
      }),
      h('span', {
        class: forecast.slipDays > 0 ? 'works__stat works__stat--draft' : 'works__stat',
        text: `forecast ${slipWord(forecast.slipDays)}`,
      }),
    ),

    /* FOLDED SHUT BY DEFAULT HERE TOO, and on a report that is the stronger
       case: this document is printed, and the Gantt is a scrolling view of a
       few hundred rows in a fixed box. A reader who wants the bars opens
       them on screen; what goes on paper is the curve and the late list,
       which are the figures 4.21(h) actually asks for. */
    ganttShown(s) ? ganttPanel(s) : null,

    h('div', {}, h('div', { class: 'works__sub-head', text: 'Forecast completion' }), outlook),

    /* The Charts page's S-curve, as at the period's end (LC-55), with the
       table under it for the figures. */
    sCurveChart(s.baseline, rows, asOf),
    curve.length > 0 ? curveTable(curve) : null,

    lateTable
      ? h(
          'div',
          {},
          h('div', { class: 'works__sub-head', text: 'Behind the programme, least float first' }),
          lateTable,
        )
      : h('p', { class: 'wrep__gap', text: 'Nothing is behind the programme.' }),
  );
}

/**
 * Forecast completion against the baseline and the contract, and the critical
 * path as it stands (LC-44): three dates, two slips, and the activities with no
 * float left, at most twenty.
 */
function forecastBlock(f: Forecast, contract: string | null): HTMLElement {
  const againstContract = contract === null ? null : signedDays(contract, f.forecastFinish);
  return h(
    'div',
    { class: 'plan__forecast', 'data-slip': String(f.slipDays) },
    h(
      'div',
      { class: 'det-facts' },
      fact('Status date', prettyDate(f.statusDate)),
      fact('Baseline completion', prettyDate(f.baselineFinish)),
      fact('Contract completion', contract === null ? '—' : prettyDate(contract)),
      fact('Forecast completion', prettyDate(f.forecastFinish)),
      fact(
        'Against the baseline',
        tag(slipWord(f.slipDays), f.slipDays > 0 ? 3 : 1, `${f.slipWorkingDays} working days`),
      ),
      fact(
        'Against the contract',
        againstContract === null ? '—' : tag(slipWord(againstContract), againstContract > 0 ? 3 : 1),
      ),
      fact('Logic', f.logic === 'links' ? 'Re-solved from the stored links' : 'No links stored: planned dates held'),
    ),
    f.criticalPath.length
      ? h(
          'table',
          { class: 'tbl', 'data-critical': String(f.criticalPath.length) },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'padding-left:14px', text: 'Critical now' }),
              h('th', { class: 'num', text: 'Forecast' }),
              h('th', { class: 'num', text: 'Planned finish' }),
              h('th', { class: 'num', text: 'Slip' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...f.criticalPath.slice(0, 20).map((r) =>
              h(
                'tr',
                {},
                h('td', { style: 'padding-left:14px', text: r.name }),
                h('td', { class: 'num', text: `${prettyDate(r.forecastStart)} → ${prettyDate(r.forecastFinish)}` }),
                h('td', { class: 'num', text: prettyDate(r.plannedFinish) }),
                h('td', { class: 'num', text: slipWord(r.slipDays) }),
              ),
            ),
          ),
        )
      : null,
  );
}

/* ------------------- the Variance page, read at a glance ------------------- */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "17 Sep", with the year only where it is not the status date's. */
function shortDay(iso: string, year: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  const y = iso.slice(0, 4) === year ? '' : ` ${iso.slice(2, 4)}`;
  return `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]}${y}`;
}

/** "2 – 9 Oct", "28 Nov – 1 Dec". */
function shortRange(from: string, to: string, year: string): string {
  if (from.slice(0, 7) === to.slice(0, 7)) {
    return from === to ? shortDay(to, year) : `${Number(from.slice(8, 10))} – ${shortDay(to, year)}`;
  }
  return `${shortDay(from, year)} – ${shortDay(to, year)}`;
}

function slipTone(days: number): string {
  return days > 0 ? 'late' : days < 0 ? 'early' : 'on';
}

/**
 * The forecast as one statement: the completion date, how late it is on the
 * baseline and on the contract, the three dates on one line, and the critical
 * path as it stands.
 */
function forecastGlance(f: Forecast, contract: string | null): HTMLElement {
  const againstContract = contract === null ? null : signedDays(contract, f.forecastFinish);
  const year = f.statusDate.slice(0, 4);
  const marks = [
    { key: 'baseline', label: 'Baseline', day: f.baselineFinish },
    ...(contract === null ? [] : [{ key: 'contract', label: 'Contract', day: contract }]),
    { key: 'forecast', label: 'Forecast', day: f.forecastFinish },
  ];
  const days = marks.map((m) => m.day).sort();
  const span = Math.max(1, signedDays(days[0]!, days.at(-1)!));
  const pad = Math.max(3, Math.round(span * 0.18));
  const total = span + 2 * pad;
  const at = (d: string) => ((signedDays(days[0]!, d) + pad) / total) * 100;
  const slipFrom = f.slipDays >= 0 ? f.baselineFinish : f.forecastFinish;
  const slipTo = f.slipDays >= 0 ? f.forecastFinish : f.baselineFinish;
  /* Two marks close together lean apart, the earlier one's words ending at its
     pin and the later one's starting at it, so the labels never overprint. */
  const lean = new Map<string, 'start' | 'end'>();
  const byPlace = [...marks].sort((a, b) => at(a.day) - at(b.day));
  for (let i = 1; i < byPlace.length; i += 1) {
    if (at(byPlace[i]!.day) - at(byPlace[i - 1]!.day) < 16) {
      if (!lean.has(byPlace[i - 1]!.key)) lean.set(byPlace[i - 1]!.key, 'end');
      lean.set(byPlace[i]!.key, 'start');
    }
  }
  const crit = f.criticalPath.slice(0, 20);
  const oneSlip = crit.length > 0 && crit.every((r) => r.slipDays === crit[0]!.slipDays) ? crit[0]!.slipDays : null;

  return h(
    'div',
    { class: 'var', 'data-slip': String(f.slipDays) },
    h(
      'div',
      { class: 'var__head' },
      h(
        'div',
        { class: 'var__big' },
        h('span', { class: 'var__big-k', text: 'Forecast completion' }),
        h('span', { class: 'var__big-v num', text: prettyDate(f.forecastFinish) }),
      ),
      h(
        'div',
        { class: 'var__chips' },
        h(
          'span',
          { class: ['var__chip', `var__chip--${slipTone(f.slipDays)}`], title: `${f.slipWorkingDays} working days` },
          h('b', { class: 'num', text: slipWord(f.slipDays) }),
          h('span', { text: 'on the baseline' }),
        ),
        againstContract === null
          ? null
          : h(
              'span',
              { class: ['var__chip', `var__chip--${slipTone(againstContract)}`, againstContract > 0 && 'var__chip--contract'] },
              h('b', { class: 'num', text: slipWord(againstContract) }),
              h('span', { text: 'on the contract' }),
            ),
      ),
    ),
    h(
      'div',
      { class: 'var__line' },
      h('div', { class: 'var__rule' }),
      f.slipDays !== 0
        ? h('div', {
            class: ['var__slip', f.slipDays < 0 && 'var__slip--early'],
            style: { left: `${at(slipFrom)}%`, width: `${at(slipTo) - at(slipFrom)}%` },
          })
        : null,
      ...marks.map((m) =>
        h(
          'div',
          {
            class: ['var__mark', `var__mark--${m.key}`, lean.has(m.key) && `var__mark--${lean.get(m.key)}`],
            style: { left: `${at(m.day)}%` },
          },
          h('span', { class: 'var__mark-k', text: m.label }),
          h('i', { class: 'var__pin' }),
          h('span', { class: 'var__mark-v num', text: shortDay(m.day, year) }),
        ),
      ),
    ),
    crit.length
      ? h(
          'div',
          { class: 'var__list', 'data-critical': String(f.criticalPath.length) },
          h(
            'div',
            { class: 'var__list-h' },
            h('span', { class: 'var__list-t', text: 'Critical now' }),
            h('span', {
              class: 'var__list-n',
              text:
                `${f.criticalPath.length} ${f.criticalPath.length === 1 ? 'activity' : 'activities'}` +
                (oneSlip === null ? '' : ` · ${crit.length === 1 ? '' : 'each '}${slipWord(oneSlip)}`),
            }),
          ),
          h(
            'div',
            { class: ['var__row', 'var__row--head', oneSlip === null && 'var__row--slip'] },
            h('span', { text: 'Activity' }),
            h('span', { text: 'Forecast' }),
            h('span', { class: 'var__r', text: 'Was due' }),
            oneSlip === null ? h('span', { class: 'var__r', text: 'Slip' }) : null,
          ),
          ...crit.map((r) =>
            h(
              'div',
              { class: ['var__row', oneSlip === null && 'var__row--slip'] },
              h('span', { class: 'var__name', text: r.name }),
              h('span', { class: 'num', text: shortRange(r.forecastStart, r.forecastFinish, year) }),
              h('span', { class: 'num var__r var__dim', text: shortDay(r.plannedFinish, year) }),
              oneSlip === null ? h('span', { class: 'num var__r var__warn', text: slipWord(r.slipDays) }) : null,
            ),
          ),
        )
      : null,
  );
}

/**
 * The S-curve, monthly, drawn to the panel's width: planned dashed, actual
 * solid, the gap between them shaded, and the latest month's two figures said
 * in words above it.
 */
function curveGlance(curve: ReturnType<typeof progressCurve>): HTMLElement {
  const n = curve.length;
  const x = (i: number) => (n === 1 ? 50 : (i / (n - 1)) * 100);
  const y = (v: number) => 100 - v;
  const actual = curve.filter((p) => p.actual !== null);
  const last = actual.at(-1) ?? null;
  const lastAt = last ? curve.indexOf(last) : -1;
  const gap = last ? Math.round(((last.actual ?? 0) - last.planned) * 10) / 10 : 0;
  const planned = curve.map((p, i) => `${x(i)},${y(p.planned)}`);
  const done = actual.map((p) => `${x(curve.indexOf(p))},${y(p.actual!)}`);
  const band = done.length > 1 ? [...planned.slice(0, done.length), ...[...done].reverse()].join(' ') : '';
  const every = Math.max(1, Math.ceil(n / 9));
  const label = (m: string) => {
    const mi = Number(m.slice(5, 7)) - 1;
    return mi === 0 ? `Jan ${m.slice(2, 4)}` : MONTH_ABBR[mi]!;
  };
  const line = (points: string[], cls: string) =>
    sv('polyline', { points: points.join(' '), class: cls, fill: 'none', 'vector-effect': 'non-scaling-stroke' });

  return h(
    'div',
    { class: 'var var--curve' },
    h(
      'div',
      { class: 'var__legend' },
      last
        ? h(
            'span',
            { class: 'var__legend-t' },
            h('span', { text: `${label(last.month)}: ` }),
            h('b', { class: 'num', text: `${last.actual}%` }),
            h('span', { text: ' actual of ' }),
            h('b', { class: 'num', text: `${last.planned}%` }),
            h('span', { text: ' planned' }),
            gap !== 0 ? h('span', { class: ['var__gap', gap < 0 && 'var__gap--behind', 'num'], text: `${gap > 0 ? '+' : '−'}${Math.abs(gap)} pts` }) : null,
          )
        : h('span', { class: 'var__legend-t', text: 'Nothing declared complete yet' }),
      h('span', { class: 'var__key' }, h('i', { class: 'var__swatch var__swatch--plan' }), h('span', { text: 'Planned' })),
      h('span', { class: 'var__key' }, h('i', { class: 'var__swatch var__swatch--actual' }), h('span', { text: 'Actual' })),
    ),
    h(
      'div',
      { class: 'var__chart' },
      h('span', { class: 'var__y var__y--top num', text: '100%' }),
      h('span', { class: 'var__y var__y--mid num', text: '50' }),
      h(
        'div',
        { class: 'var__plot' },
        sv(
          'svg',
          {
            viewBox: '0 0 100 100',
            preserveAspectRatio: 'none',
            class: 'var__svg',
            role: 'img',
            'aria-label': last
              ? `Cumulative progress by month. ${label(last.month)}: ${last.actual}% actual against ${last.planned}% planned.`
              : 'Cumulative planned progress by month.',
          },
          sv('line', { x1: 0, x2: 100, y1: 50, y2: 50, class: 'var__grid', 'vector-effect': 'non-scaling-stroke' }),
          sv('line', { x1: 0, x2: 100, y1: 100, y2: 100, class: 'var__grid', 'vector-effect': 'non-scaling-stroke' }),
          sv('line', { x1: 0, x2: 100, y1: 0, y2: 0, class: 'var__grid', 'vector-effect': 'non-scaling-stroke' }),
          band ? sv('polygon', { points: band, class: 'var__band' }) : null,
          line(planned, 'var__plan'),
          done.length > 1 ? line(done, 'var__actual') : null,
        ),
        last ? h('i', { class: 'var__dot', style: { left: `${x(lastAt)}%`, top: `${y(last.actual!)}%` } }) : null,
      ),
      h(
        'div',
        { class: 'var__months' },
        ...curve.map((p, i) =>
          i % every === 0 || i === n - 1 ? h('span', { class: 'num', style: { left: `${x(i)}%` }, text: label(p.month) }) : null,
        ),
      ),
    ),
  );
}

/** The late activities, least float first — at most twenty, a row each. */
function lateGlance(late: ReturnType<typeof lateRows>, asOf: string): HTMLElement {
  const year = asOf.slice(0, 4);
  /* Where every one of them is critical, the list says so once rather than on each row. */
  const allCritical = late.every((r) => r.floatDays === 0);
  return h(
    'div',
    { class: 'var var--late' },
    h('div', {
      class: 'var__list-n var__late-n',
      text: `${late.length} ${late.length === 1 ? 'activity' : 'activities'}${allCritical ? `, ${late.length === 1 ? 'on' : 'all on'} the critical path` : ''}`,
    }),
    h(
      'div',
      { class: 'var__row var__row--late var__row--head' },
      h('span', { text: 'Activity' }),
      h('span', { text: 'Was due' }),
      h('span', { text: 'State' }),
      h('span', { class: 'var__r', text: 'Late by' }),
    ),
    ...late.slice(0, 20).map((r) =>
      h(
        'div',
        { class: 'var__row var__row--late' },
        h(
          'span',
          { class: 'var__name' },
          h('span', { text: r.name }),
          allCritical
            ? null
            : r.floatDays === 0
              ? h('span', { class: 'var__crit', text: 'Critical' })
              : h('span', { class: 'var__float num', text: `${Math.round(r.floatDays)} d float` }),
        ),
        h('span', { class: 'num var__dim', text: shortDay(r.plannedFinish, year) }),
        h('span', { class: ['var__state', `var__state--${r.state}`], text: STATE_WORD[r.state] }),
        h('span', { class: 'num var__r var__warn', text: `${latenessOf(r, asOf)} d` }),
      ),
    ),
    late.length > 20 ? h('div', { class: 'var__more muted', text: `and ${late.length - 20} more` }) : null,
  );
}

/** The late activities, least float first — at most twenty. */
function lateList(late: ReturnType<typeof lateRows>, asOf: string): HTMLElement {
  return h(
    'table',
    { class: 'tbl' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'Activity' }),
        h('th', { class: 'num', text: 'Planned finish' }),
        h('th', { class: 'num', text: 'Float' }),
        h('th', { text: 'State' }),
        h('th', { class: 'num', text: 'Late by' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...late.slice(0, 20).map((r) =>
        h(
          'tr',
          {},
          h('td', { style: 'padding-left:14px', text: r.name }),
          h('td', { class: 'num', text: r.plannedFinish }),
          h('td', { class: 'num', text: r.floatDays === 0 ? 'critical' : `${Math.round(r.floatDays)} d` }),
          h('td', { text: STATE_WORD[r.state] }),
          h('td', { class: 'num', text: `${latenessOf(r, asOf)} d` }),
        ),
      ),
    ),
  );
}

/** A section that folds shut under its head, remembered as `collapsedGroups` are. */
function fold(s: Readonly<State>, name: string, title: string, body: HTMLElement): HTMLElement {
  const collapsed = s.collapsedGroups.includes(name);
  return h(
    'div',
    { class: 'det-block det-block--flush', 'data-group': name },
    h(
      'button',
      {
        type: 'button',
        class: 'grp__head',
        'aria-expanded': String(!collapsed),
        onclick: () => actions.toggleGroup(name),
      },
      icon(ICONS.chevronDown, {
        size: 10,
        width: 2,
        class: collapsed ? 'grp__chev grp__chev--closed' : 'grp__chev',
      }),
      title,
    ),
    collapsed ? null : body,
  );
}

/**
 * The S-curve, as a table with a bar per month.
 *
 * THE TABLE STAYS UNDER THE CHART (LC-55). The report draws the Charts page's
 * SVG curve above it, which prints, and goes into Word as a picture; this is
 * the figures, and a bar drawn from a CSS width that reads in black and white
 * on any printer. The numbers are on the row beside the bar, which is what a
 * reader checking a figure actually wants.
 *
 * A STAIRCASE RATHER THAN A SMOOTH S, because an activity counts on its finish
 * — see `progressCurve`. That is the honest appearance of a programme measured
 * in completed activities rather than in spent value.
 */
function curveTable(curve: ReturnType<typeof progressCurve>, head = true): HTMLElement {
  return h(
    'div',
    {},
    head ? h('div', { class: 'works__sub-head', text: 'Planned against actual, cumulative' }) : null,
    h(
      'table',
      { class: 'tbl' },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Month' }),
          h('th', { class: 'num', text: 'Planned' }),
          h('th', { class: 'num', text: 'Actual' }),
          h('th', { text: '' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...curve.map((p) =>
          h(
            'tr',
            {},
            h('td', { style: 'padding-left:14px', text: p.month }),
            h('td', { class: 'num', text: `${p.planned}%` }),
            /* NULL IS A FUTURE MONTH, NOT ZERO. A curve that ran the actual
               line along the floor to the end of the programme would read as a
               project that has stopped. */
            h('td', { class: 'num', text: p.actual === null ? '' : `${p.actual}%` }),
            h(
              'td',
              { class: 'prog__barcell' },
              h('span', { class: 'prog__bar prog__bar--planned', style: `width:${p.planned}%` }),
              p.actual === null
                ? null
                : h('span', { class: 'prog__bar prog__bar--actual', style: `width:${p.actual}%` }),
            ),
          ),
        ),
      ),
    ),
  );
}

/* --------------------------- Monitoring's property panel --------------------------- */

/** How many of the activities planned to start next are listed. */
const NEXT_SHOWN = 10;

/**
 * THE PLANNED ACTIVITY, for Construction Monitoring's property panel beside the
 * site record: what the frozen programme has on site now, what it had finished
 * by now and the record does not show finished, and what it starts next. Read,
 * not managed — matching and declaring finishes are the Scheduler's.
 *
 * AN OPEN HOLD POINT SHOWS AGAINST THE WORK IT HOLDS (LC-54): its reference on
 * the activity's row wherever that row is listed, and every open hold point in
 * a fold of its own first, so one holding an activity not listed here — not yet
 * due, or declared complete — is still seen.
 */
export function plannedActivity(s: Readonly<State>): HTMLElement {
  if (s.progressRead !== 'read') {
    return emptyState(
      s.progressRead === 'unreachable'
        ? 'The programme could not be read.'
        : 'The programme has not been read yet.',
    );
  }
  if (s.baseline === null) return emptyState('No programme has been frozen for this structure.');

  const asOf = today();
  const paths = wbsPaths(s.baseline);
  const rows = varianceFor(s.baseline, s.matches, countable(s)).filter(
    (r) => r.plannedDays > 0 && r.state !== 'complete',
  );
  const byStart = (a: VarianceRow, b: VarianceRow) => a.plannedStart.localeCompare(b.plannedStart);
  const overdue = rows.filter((r) => r.plannedFinish < asOf).sort(byStart);
  const now = rows.filter((r) => r.plannedStart <= asOf && r.plannedFinish >= asOf).sort(byStart);
  const next = rows.filter((r) => r.plannedStart > asOf).sort(byStart);
  const holds = holdsByActivity(s.items);
  const held = [...holds.values()].flat();
  const shown = next.slice(0, NEXT_SHOWN);
  const axis = axisOf([...overdue, ...now, ...shown], asOf);

  return h(
    'div',
    { class: 'plan' },
    held.length
      ? fold(s, 'plan-held', counted('On hold', held.length), heldTable(held, s.baseline))
      : null,
    /* OVERDUE COMES FIRST. What the programme had finished by now and the
       record does not show finished is the furthest behind, so it is read
       before what is merely due. */
    overdue.length
      ? fold(
          s,
          'plan-overdue',
          counted('Overdue', overdue.length),
          plannedTable(overdue, paths, asOf, axis, undefined, holds),
        )
      : null,
    fold(
      s,
      'plan-now',
      counted('Planned now', now.length),
      plannedTable(now, paths, asOf, axis, 'Nothing is planned to be under way today.', holds),
    ),
    fold(
      s,
      'plan-next',
      next.length > NEXT_SHOWN ? `Next · ${NEXT_SHOWN} of ${next.length}` : counted('Next', next.length),
      plannedTable(shown, paths, asOf, axis, 'Nothing more is planned to start.', holds),
    ),
  );
}

/**
 * THE FOLDS SCROLL SIDEWAYS TOGETHER. A panel too narrow for every column
 * scrolls each table in its card; one scrolled alone would put its timeline out
 * of line with the others', so the rest follow it.
 */
function scrollTogether(e: Event): void {
  const from = e.currentTarget as HTMLElement;
  for (const el of from.closest('.plan')?.querySelectorAll<HTMLElement>('.plan__scroll') ?? []) {
    if (el !== from && el.scrollLeft !== from.scrollLeft) el.scrollLeft = from.scrollLeft;
  }
}

/** Days to a date, for placing it on the panel's timeline. */
function dayOf(date: string): number {
  return Date.parse(`${date}T00:00:00Z`) / 86_400_000;
}

/**
 * THE PANEL'S ONE TIME AXIS: from the first listed start (or today) to the last
 * listed finish, shared by every fold so a bar in one lines up with a bar in
 * another, and today falls at the same place in each.
 */
interface Axis {
  from: number;
  to: number;
  today: number;
}

function axisOf(rows: readonly VarianceRow[], asOf: string): Axis {
  const today = dayOf(asOf);
  let from = today;
  let to = today + 1;
  for (const r of rows) {
    from = Math.min(from, dayOf(r.plannedStart));
    to = Math.max(to, dayOf(r.plannedFinish) + 1);
  }
  return { from, to, today };
}

function at(axis: Axis, day: number): number {
  return Math.max(0, Math.min(100, ((day - axis.from) / (axis.to - axis.from)) * 100));
}

/** The months along the axis, as the timeline column's head. */
function axisHead(axis: Axis): HTMLElement {
  const months: HTMLElement[] = [];
  const first = new Date(axis.from * 86_400_000);
  let y = first.getUTCFullYear();
  let m = first.getUTCMonth();
  for (;;) {
    const start = Math.max(axis.from, Date.UTC(y, m, 1) / 86_400_000);
    if (start >= axis.to) break;
    const end = Math.min(axis.to, Date.UTC(y, m + 1, 1) / 86_400_000);
    months.push(
      h('span', {
        class: 'plan__month',
        style: `left:${at(axis, start)}%;width:${at(axis, end) - at(axis, start)}%`,
        text: prettyDate(new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10)).split(' ')[1]!,
      }),
    );
    m += 1;
    if (m === 12) [y, m] = [y + 1, 0];
  }
  return h('div', { class: 'plan__axis' }, ...months, h('span', { class: 'plan__today', style: `left:${at(axis, axis.today)}%` }));
}

function counted(head: string, n: number): string {
  return n ? `${head} · ${n}` : head;
}

/** The open hold points, each against the activity it holds. */
function heldTable(held: readonly SiteItem[], baseline: Baseline): HTMLElement {
  const names = new Map(baseline.activities.map((a) => [a.activityId, a.name]));
  return h(
    'table',
    { class: 'tbl' },
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { style: 'padding-left:14px', text: 'Activity' }),
        h('th', { text: 'Hold point' }),
        h('th', { class: 'num', text: 'Due' }),
      ),
    ),
    h(
      'tbody',
      {},
      ...held.map((i) =>
        h(
          'tr',
          { 'data-held': i.activityId ?? '' },
          h('td', { style: 'padding-left:14px', text: names.get(i.activityId ?? '') ?? i.activityName ?? '' }),
          h('td', { title: i.title }, tag(i.ref, 3), h('span', { class: 'muted', text: ` ${i.title}` })),
          h('td', { class: 'num', text: i.dueOn ? prettyDate(i.dueOn) : '—' }),
        ),
      ),
    ),
  );
}

function plannedTable(
  rows: readonly VarianceRow[],
  paths: ReadonlyMap<string, string[]>,
  asOf: string,
  axis: Axis,
  none?: string,
  holds: ReadonlyMap<string, SiteItem[]> = new Map(),
): HTMLElement {
  return h(
    'div',
    { class: 'plan__scroll', onscroll: scrollTogether },
    rows.length === 0
      ? h('p', { class: 'muted plan__none', text: none ?? '' })
      : h(
          'table',
          { class: 'tbl plan__tbl' },
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { style: 'padding-left:14px', text: 'Activity' }),
              h('th', { class: 'plan__dates', text: 'Planned' }),
              h('th', { class: 'plan__tl', 'aria-label': 'Timeline' }, axisHead(axis)),
              h('th', { class: 'num plan__state', text: 'State' }),
            ),
          ),
          h(
            'tbody',
            {},
            ...rows.map((r) => {
              const path = paths.get(r.activityId) ?? [];
              const lateBy = latenessOf(r, asOf);
              const start = dayOf(r.plannedStart);
              const end = dayOf(r.plannedFinish) + 1;
              /* LATE PAST TWO WEEKS READS STRONGER than a few days behind, so the
                 worst of the list is the first thing the eye lands on. */
              const severity = lateBy > 14 ? 'crit' : lateBy > 0 ? 'late' : 'due';
              const startsIn = start - axis.today;
              return h(
                'tr',
                { 'data-late': String(lateBy) },
                h(
                  'td',
                  { style: 'padding-left:14px', title: [...path, r.name].join(' › ') },
                  h('div', { text: r.name }),
                  path.length ? h('div', { class: 'muted plan__wbs', text: path[path.length - 1]! }) : null,
                  ...(holds.get(r.activityId) ?? []).map((i) =>
                    h('div', { 'data-hold': i.ref }, tag(`${i.ref} hold point`, 3, i.title)),
                  ),
                ),
                h(
                  'td',
                  { class: 'plan__dates' },
                  h('div', { text: `${prettyDate(r.plannedStart)} → ${prettyDate(r.plannedFinish)}` }),
                  h('div', { class: 'muted plan__wbs', text: `${end - start} d` }),
                ),
                h(
                  'td',
                  { class: 'plan__tl' },
                  h(
                    'div',
                    { class: 'plan__track' },
                    h('span', {
                      class: ['plan__bar', `plan__bar--${startsIn > 0 ? 'next' : severity}`],
                      style: `left:${at(axis, start)}%;width:${Math.max(1.5, at(axis, end) - at(axis, start))}%`,
                    }),
                    h('span', { class: 'plan__today', style: `left:${at(axis, axis.today)}%` }),
                  ),
                ),
                h(
                  'td',
                  { class: 'num plan__state' },
                  lateBy > 0
                    ? h('span', {
                        class: ['works__stat', severity === 'crit' ? 'plan__late--crit' : 'works__stat--draft'],
                        text: `${lateBy} d late`,
                      })
                    : null,
                  h('div', {
                    class: 'muted plan__wbs',
                    text:
                      r.state === 'not-started' && startsIn > 0
                        ? `Starts in ${startsIn} d`
                        : STATE_WORD[r.state],
                  }),
                ),
              );
            }),
          ),
        ),
  );
}
